/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  Renderer2,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {
  applyCssTransform,
  coerceBooleanProperty,
  FocusOrigin,
  FocusOriginMonitor,
  HammerInput,
  MdRipple,
  RippleRef,
  Platform,
} from '../core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {mixinDisabled, CanDisable} from '../core/common-behaviors/disabled';
import {CanColor, mixinColor} from '../core/common-behaviors/color';


export const /** @type {?} */ MD_SLIDE_TOGGLE_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MdSlideToggle),
  multi: true
};
export class MdSlideToggleChange {
  source: MdSlideToggle;
  checked: boolean;
}

function MdSlideToggleChange_tsickle_Closure_declarations() {
/** @type {?} */
MdSlideToggleChange.prototype.source;
/** @type {?} */
MdSlideToggleChange.prototype.checked;
}


// Increasing integer for generating unique ids for slide-toggle components.
let /** @type {?} */ nextId = 0;
export class MdSlideToggleBase {
/**
 * @param {?} _renderer
 * @param {?} _elementRef
 */
constructor(public _renderer: Renderer2,
public _elementRef: ElementRef) {}
}

function MdSlideToggleBase_tsickle_Closure_declarations() {
/** @type {?} */
MdSlideToggleBase.prototype._renderer;
/** @type {?} */
MdSlideToggleBase.prototype._elementRef;
}

export const /** @type {?} */ _MdSlideToggleMixinBase = mixinColor(mixinDisabled(MdSlideToggleBase), 'accent');
/**
 * Represents a slidable "switch" toggle that can be moved between on and off.
 */
export class MdSlideToggle extends _MdSlideToggleMixinBase
    implements OnDestroy, AfterContentInit, ControlValueAccessor, CanDisable, CanColor {
private onChange = (_: any) => {};
private onTouched = () => {};
private _uniqueId = `md-slide-toggle-${++nextId}`;
private _checked: boolean = false;
private _slideRenderer: SlideToggleRenderer = null;
private _required: boolean = false;
private _disableRipple: boolean = false;
/**
 * Reference to the focus state ripple.
 */
private _focusRipple: RippleRef;
/**
 * Name value will be applied to the input element if present
 */
name: string = null;
/**
 * A unique id for the slide-toggle input. If none is supplied, it will be auto-generated.
 */
id: string = this._uniqueId;
/**
 * Used to specify the tabIndex value for the underlying input element.
 */
tabIndex: number = 0;
/**
 * Whether the label should appear after or before the slide-toggle. Defaults to 'after'
 */
labelPosition: 'before' | 'after' = 'after';
/**
 * Used to set the aria-label attribute on the underlying input element.
 */
ariaLabel: string = null;
/**
 * Used to set the aria-labelledby attribute on the underlying input element.
 */
ariaLabelledby: string = null;
/**
 * Whether the slide-toggle is required.
 * @return {?}
 */
get required(): boolean { return this._required; }
/**
 * @param {?} value
 * @return {?}
 */
set required(value) { this._required = coerceBooleanProperty(value); }
/**
 * Whether the ripple effect for this slide-toggle is disabled.
 * @return {?}
 */
get disableRipple(): boolean { return this._disableRipple; }
/**
 * @param {?} value
 * @return {?}
 */
set disableRipple(value) { this._disableRipple = coerceBooleanProperty(value); }
/**
 * An event will be dispatched each time the slide-toggle changes its value.
 */
change: EventEmitter<MdSlideToggleChange> = new EventEmitter<MdSlideToggleChange>();
/**
 * Returns the unique id for the visual hidden input.
 * @return {?}
 */
get inputId(): string { return `${this.id || this._uniqueId}-input`; }
/**
 * Reference to the underlying input element.
 */
_inputElement: ElementRef;
/**
 * Reference to the ripple directive on the thumb container.
 */
_ripple: MdRipple;
/**
 * @param {?} elementRef
 * @param {?} renderer
 * @param {?} _platform
 * @param {?} _focusOriginMonitor
 * @param {?} _changeDetectorRef
 */
constructor(elementRef: ElementRef,
              renderer: Renderer2,
private _platform: Platform,
private _focusOriginMonitor: FocusOriginMonitor,
private _changeDetectorRef: ChangeDetectorRef) {
    super(renderer, elementRef);
  }
/**
 * @return {?}
 */
ngAfterContentInit() {
    this._slideRenderer = new SlideToggleRenderer(this._elementRef, this._platform);

    this._focusOriginMonitor
      .monitor(this._inputElement.nativeElement, this._renderer, false)
      .subscribe(focusOrigin => this._onInputFocusChange(focusOrigin));
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    this._focusOriginMonitor.stopMonitoring(this._inputElement.nativeElement);
  }
/**
 * The onChangeEvent method will be also called on click.
 * This is because everything for the slide-toggle is wrapped inside of a label,
 * which triggers a onChange event on click.
 * @param {?} event
 * @return {?}
 */
_onChangeEvent(event: Event) {
    // We always have to stop propagation on the change event.
    // Otherwise the change event, from the input element, will bubble up and
    // emit its event object to the component's `change` output.
    event.stopPropagation();

    // Once a drag is currently in progress, we do not want to toggle the slide-toggle on a click.
    if (!this.disabled && !this._slideRenderer.dragging) {
      this.toggle();

      // Emit our custom change event if the native input emitted one.
      // It is important to only emit it, if the native input triggered one, because
      // we don't want to trigger a change event, when the `checked` variable changes for example.
      this._emitChangeEvent();
    }
  }
/**
 * @param {?} event
 * @return {?}
 */
_onInputClick(event: Event) {
    this.onTouched();

    // We have to stop propagation for click events on the visual hidden input element.
    // By default, when a user clicks on a label element, a generated click event will be
    // dispatched on the associated input element. Since we are using a label element as our
    // root container, the click event on the `slide-toggle` will be executed twice.
    // The real click event will bubble up, and the generated click event also tries to bubble up.
    // This will lead to multiple click events.
    // Preventing bubbling for the second event will solve that issue.
    event.stopPropagation();
  }
/**
 * Implemented as part of ControlValueAccessor.
 * @param {?} value
 * @return {?}
 */
writeValue(value: any): void {
    this.checked = value;
  }
/**
 * Implemented as part of ControlValueAccessor.
 * @param {?} fn
 * @return {?}
 */
registerOnChange(fn: any): void {
    this.onChange = fn;
  }
/**
 * Implemented as part of ControlValueAccessor.
 * @param {?} fn
 * @return {?}
 */
registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
/**
 * Implemented as a part of ControlValueAccessor.
 * @param {?} isDisabled
 * @return {?}
 */
setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this._changeDetectorRef.markForCheck();
  }
/**
 * Focuses the slide-toggle.
 * @return {?}
 */
focus() {
    this._focusOriginMonitor.focusVia(this._inputElement.nativeElement, 'keyboard');
  }
/**
 * Whether the slide-toggle is checked.
 * @return {?}
 */
get checked() { return !!this._checked; }
/**
 * @param {?} value
 * @return {?}
 */
set checked(value) {
    if (this.checked !== !!value) {
      this._checked = value;
      this.onChange(this._checked);
    }
  }
/**
 * Toggles the checked state of the slide-toggle.
 * @return {?}
 */
toggle() {
    this.checked = !this.checked;
  }
/**
 * Function is called whenever the focus changes for the input element.
 * @param {?} focusOrigin
 * @return {?}
 */
private _onInputFocusChange(focusOrigin: FocusOrigin) {
    if (!this._focusRipple && focusOrigin === 'keyboard') {
      // For keyboard focus show a persistent ripple as focus indicator.
      this._focusRipple = this._ripple.launch(0, 0, {persistent: true, centered: true});
    } else if (!focusOrigin) {
      this.onTouched();

      // Fade out and clear the focus ripple if one is currently present.
      if (this._focusRipple) {
        this._focusRipple.fadeOut();
        this._focusRipple = null;
      }
    }
  }
/**
 * Emits the change event to the `change` output EventEmitter
 * @return {?}
 */
private _emitChangeEvent() {
    let /** @type {?} */ event = new MdSlideToggleChange();
    event.source = this;
    event.checked = this.checked;
    this.change.emit(event);
  }
/**
 * @return {?}
 */
_onDragStart() {
    if (!this.disabled) {
      this._slideRenderer.startThumbDrag(this.checked);
    }
  }
/**
 * @param {?} event
 * @return {?}
 */
_onDrag(event: HammerInput) {
    if (this._slideRenderer.dragging) {
      this._slideRenderer.updateThumbPosition(event.deltaX);
    }
  }
/**
 * @return {?}
 */
_onDragEnd() {
    if (this._slideRenderer.dragging) {
      let /** @type {?} */ _previousChecked = this.checked;
      this.checked = this._slideRenderer.dragPercentage > 50;

      if (_previousChecked !== this.checked) {
        this._emitChangeEvent();
      }

      // The drag should be stopped outside of the current event handler, because otherwise the
      // click event will be fired before and will revert the drag change.
      setTimeout(() => this._slideRenderer.stopThumbDrag());
    }
  }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-slide-toggle, mat-slide-toggle',
  host: {
    'class': 'mat-slide-toggle',
    '[class.mat-checked]': 'checked',
    '[class.mat-disabled]': 'disabled',
    '[class.mat-slide-toggle-label-before]': 'labelPosition == "before"',
  },
  templateUrl: 'slide-toggle.html',
  styleUrls: ['slide-toggle.css'],
  providers: [MD_SLIDE_TOGGLE_VALUE_ACCESSOR],
  inputs: ['disabled', 'color'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: Renderer2, },
{type: Platform, },
{type: FocusOriginMonitor, },
{type: ChangeDetectorRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'name': [{ type: Input },],
'id': [{ type: Input },],
'tabIndex': [{ type: Input },],
'labelPosition': [{ type: Input },],
'ariaLabel': [{ type: Input, args: ['aria-label', ] },],
'ariaLabelledby': [{ type: Input, args: ['aria-labelledby', ] },],
'required': [{ type: Input },],
'disableRipple': [{ type: Input },],
'change': [{ type: Output },],
'_inputElement': [{ type: ViewChild, args: ['input', ] },],
'_ripple': [{ type: ViewChild, args: [MdRipple, ] },],
'checked': [{ type: Input },],
};
}

function MdSlideToggle_tsickle_Closure_declarations() {
/** @type {?} */
MdSlideToggle.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdSlideToggle.ctorParameters;
/** @type {?} */
MdSlideToggle.propDecorators;
/** @type {?} */
MdSlideToggle.prototype.onChange;
/** @type {?} */
MdSlideToggle.prototype.onTouched;
/** @type {?} */
MdSlideToggle.prototype._uniqueId;
/** @type {?} */
MdSlideToggle.prototype._checked;
/** @type {?} */
MdSlideToggle.prototype._slideRenderer;
/** @type {?} */
MdSlideToggle.prototype._required;
/** @type {?} */
MdSlideToggle.prototype._disableRipple;
/**
 * Reference to the focus state ripple.
 * @type {?}
 */
MdSlideToggle.prototype._focusRipple;
/**
 * Name value will be applied to the input element if present
 * @type {?}
 */
MdSlideToggle.prototype.name;
/**
 * A unique id for the slide-toggle input. If none is supplied, it will be auto-generated.
 * @type {?}
 */
MdSlideToggle.prototype.id;
/**
 * Used to specify the tabIndex value for the underlying input element.
 * @type {?}
 */
MdSlideToggle.prototype.tabIndex;
/**
 * Whether the label should appear after or before the slide-toggle. Defaults to 'after'
 * @type {?}
 */
MdSlideToggle.prototype.labelPosition;
/**
 * Used to set the aria-label attribute on the underlying input element.
 * @type {?}
 */
MdSlideToggle.prototype.ariaLabel;
/**
 * Used to set the aria-labelledby attribute on the underlying input element.
 * @type {?}
 */
MdSlideToggle.prototype.ariaLabelledby;
/**
 * An event will be dispatched each time the slide-toggle changes its value.
 * @type {?}
 */
MdSlideToggle.prototype.change;
/**
 * Reference to the underlying input element.
 * @type {?}
 */
MdSlideToggle.prototype._inputElement;
/**
 * Reference to the ripple directive on the thumb container.
 * @type {?}
 */
MdSlideToggle.prototype._ripple;
/** @type {?} */
MdSlideToggle.prototype._platform;
/** @type {?} */
MdSlideToggle.prototype._focusOriginMonitor;
/** @type {?} */
MdSlideToggle.prototype._changeDetectorRef;
}

/**
 * Renderer for the Slide Toggle component, which separates DOM modification in its own class
 */
class SlideToggleRenderer {
/**
 * Reference to the thumb HTMLElement.
 */
private _thumbEl: HTMLElement;
/**
 * Reference to the thumb bar HTMLElement.
 */
private _thumbBarEl: HTMLElement;
/**
 * Width of the thumb bar of the slide-toggle.
 */
private _thumbBarWidth: number;
/**
 * Previous checked state before drag started.
 */
private _previousChecked: boolean;
/**
 * Percentage of the thumb while dragging. Percentage as fraction of 100.
 */
dragPercentage: number;
/**
 * Whether the thumb is currently being dragged.
 */
dragging: boolean = false;
/**
 * @param {?} _elementRef
 * @param {?} platform
 */
constructor(private _elementRef: ElementRef, platform: Platform) {
    // We only need to interact with these elements when we're on the browser, so only grab
    // the reference in that case.
    if (platform.isBrowser) {
      this._thumbEl = _elementRef.nativeElement.querySelector('.mat-slide-toggle-thumb-container');
      this._thumbBarEl = _elementRef.nativeElement.querySelector('.mat-slide-toggle-bar');
    }
  }
/**
 * Initializes the drag of the slide-toggle.
 * @param {?} checked
 * @return {?}
 */
startThumbDrag(checked: boolean) {
    if (this.dragging) { return; }

    this._thumbBarWidth = this._thumbBarEl.clientWidth - this._thumbEl.clientWidth;
    this._thumbEl.classList.add('mat-dragging');

    this._previousChecked = checked;
    this.dragging = true;
  }
/**
 * Resets the current drag and returns the new checked value.
 * @return {?}
 */
stopThumbDrag(): boolean {
    if (!this.dragging) { return; }

    this.dragging = false;
    this._thumbEl.classList.remove('mat-dragging');

    // Reset the transform because the component will take care of the thumb position after drag.
    applyCssTransform(this._thumbEl, '');

    return this.dragPercentage > 50;
  }
/**
 * Updates the thumb containers position from the specified distance.
 * @param {?} distance
 * @return {?}
 */
updateThumbPosition(distance: number) {
    this.dragPercentage = this._getDragPercentage(distance);
    // Calculate the moved distance based on the thumb bar width.
    let /** @type {?} */ dragX = (this.dragPercentage / 100) * this._thumbBarWidth;
    applyCssTransform(this._thumbEl, `translate3d(${dragX}px, 0, 0)`);
  }
/**
 * Retrieves the percentage of thumb from the moved distance. Percentage as fraction of 100.
 * @param {?} distance
 * @return {?}
 */
private _getDragPercentage(distance: number) {
    let /** @type {?} */ percentage = (distance / this._thumbBarWidth) * 100;

    // When the toggle was initially checked, then we have to start the drag at the end.
    if (this._previousChecked) {
      percentage += 100;
    }

    return Math.max(0, Math.min(percentage, 100));
  }

}

function SlideToggleRenderer_tsickle_Closure_declarations() {
/**
 * Reference to the thumb HTMLElement.
 * @type {?}
 */
SlideToggleRenderer.prototype._thumbEl;
/**
 * Reference to the thumb bar HTMLElement.
 * @type {?}
 */
SlideToggleRenderer.prototype._thumbBarEl;
/**
 * Width of the thumb bar of the slide-toggle.
 * @type {?}
 */
SlideToggleRenderer.prototype._thumbBarWidth;
/**
 * Previous checked state before drag started.
 * @type {?}
 */
SlideToggleRenderer.prototype._previousChecked;
/**
 * Percentage of the thumb while dragging. Percentage as fraction of 100.
 * @type {?}
 */
SlideToggleRenderer.prototype.dragPercentage;
/**
 * Whether the thumb is currently being dragged.
 * @type {?}
 */
SlideToggleRenderer.prototype.dragging;
/** @type {?} */
SlideToggleRenderer.prototype._elementRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
